import { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Calculator, DollarSign, Percent, Calendar } from 'lucide-react';

export const LoanCalculator = () => {
  const [loanAmount, setLoanAmount] = useState(300000);
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [downPayment, setDownPayment] = useState(20);
  const [homePrice, setHomePrice] = useState(375000);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [totalPayment, setTotalPayment] = useState(0);

  useEffect(() => {
    calculateMortgage();
  }, [loanAmount, interestRate, loanTerm, downPayment, homePrice]);

  const calculateMortgage = () => {
    const principal = homePrice - (homePrice * downPayment / 100);
    setLoanAmount(principal);
    
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    
    if (monthlyRate === 0) {
      const payment = principal / numberOfPayments;
      setMonthlyPayment(payment);
      setTotalPayment(principal);
      setTotalInterest(0);
      return;
    }
    
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    const total = payment * numberOfPayments;
    const interest = total - principal;
    
    setMonthlyPayment(payment);
    setTotalPayment(total);
    setTotalInterest(interest);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-lg p-6 lg:p-8" data-testid="loan-calculator">
      <div className="flex items-center mb-6">
        <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mr-4">
          <Calculator className="w-6 h-6 text-[#D4AF37]" />
        </div>
        <div>
          <h3 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: 'Playfair Display' }}>
            Loan Calculator
          </h3>
          <p className="text-[#808080] text-sm">Estimate your monthly payment</p>
        </div>
      </div>

      <div className="space-y-4">
        {/* Home Price */}
        <div>
          <label className="text-[#808080] text-sm mb-2 block">Home Price</label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
            <Input
              type="number"
              value={homePrice}
              onChange={(e) => setHomePrice(Number(e.target.value))}
              className="pl-10 bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
              data-testid="calc-home-price"
            />
          </div>
        </div>

        {/* Down Payment */}
        <div>
          <label className="text-[#808080] text-sm mb-2 block">Down Payment (%)</label>
          <div className="relative">
            <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
            <Input
              type="number"
              value={downPayment}
              onChange={(e) => setDownPayment(Number(e.target.value))}
              min="0"
              max="100"
              className="pl-10 bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
              data-testid="calc-down-payment"
            />
          </div>
          <p className="text-[#D4AF37] text-xs mt-1">
            Down Payment: {formatCurrency(homePrice * downPayment / 100)}
          </p>
        </div>

        {/* Interest Rate */}
        <div>
          <label className="text-[#808080] text-sm mb-2 block">Interest Rate (%)</label>
          <div className="relative">
            <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
            <Input
              type="number"
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              step="0.125"
              min="0"
              max="20"
              className="pl-10 bg-[#1A1A1A] border-[#333] text-[#F5F5F0]"
              data-testid="calc-interest-rate"
            />
          </div>
        </div>

        {/* Loan Term */}
        <div>
          <label className="text-[#808080] text-sm mb-2 block">Loan Term (years)</label>
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#808080]" />
            <select
              value={loanTerm}
              onChange={(e) => setLoanTerm(Number(e.target.value))}
              className="w-full pl-10 pr-4 py-2 bg-[#1A1A1A] border border-[#333] rounded-md text-[#F5F5F0]"
              data-testid="calc-loan-term"
            >
              <option value="15">15 years</option>
              <option value="20">20 years</option>
              <option value="30">30 years</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-8 pt-6 border-t border-[#D4AF37]/20">
        <div className="text-center mb-6">
          <p className="text-[#808080] text-sm mb-1">Estimated Monthly Payment</p>
          <p className="text-4xl font-light text-[#D4AF37]" style={{ fontFamily: 'Playfair Display' }} data-testid="calc-monthly-payment">
            {formatCurrency(monthlyPayment)}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#0a0a0a] p-4 rounded-lg text-center">
            <p className="text-[#808080] text-xs mb-1">Loan Amount</p>
            <p className="text-[#F5F5F0] font-semibold" data-testid="calc-loan-amount">
              {formatCurrency(loanAmount)}
            </p>
          </div>
          <div className="bg-[#0a0a0a] p-4 rounded-lg text-center">
            <p className="text-[#808080] text-xs mb-1">Total Interest</p>
            <p className="text-[#F5F5F0] font-semibold" data-testid="calc-total-interest">
              {formatCurrency(totalInterest)}
            </p>
          </div>
        </div>

        <div className="mt-4 bg-[#0a0a0a] p-4 rounded-lg text-center">
          <p className="text-[#808080] text-xs mb-1">Total of {loanTerm * 12} Payments</p>
          <p className="text-[#F5F5F0] font-semibold" data-testid="calc-total-payment">
            {formatCurrency(totalPayment)}
          </p>
        </div>

        <p className="text-[#808080] text-xs mt-4 text-center">
          *This is an estimate. Actual payments may vary based on taxes, insurance, and other factors.
        </p>
      </div>
    </div>
  );
};
